import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  BasicTable,
  Box,
  Stack,
  TableHead,
  TableRow,
  TableCell,
  ArrowDropUpIcon,
  ArrowDropDownIcon,
  TableBody
} from '@esgian/esgianui';
import { checkUserHasAccess } from '@helpers';
import { useSelector } from 'react-redux';
import { getUser, getCommercialCategoryType, getOperatorType } from '@store/features';
import { useTheme } from '@hooks/useTheme';
import { ProfileLink } from '@components/Links';
import UnderlyingCompanyPortModal from '@components/Sections/ShipAnalytics/CompanyPortStatisticsSection/UnderlyingCompanyPortModal';
import moment from 'moment';
import { getLatestPortCalls } from '@api';
import { useSegment } from '@hooks';
import { GEOGRAPHICAL_TYPES } from '@constants';

function CompanyPortTable({
  fileName = '',
  statsData = null,
  companyType = 'operator',
  companyId = null,
  showYTD = false
}) {
  const user = useSelector(getUser);
  const operatorType = useSelector(getOperatorType);
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState({});
  const [modalTitle, setModalTitle] = useState('');
  const [sortOrder, setSortOrder] = useState({
    column: 'totalCurrentYearCount',
    direction: 'desc'
  });
  const { customScrollbar, theme } = useTheme();
  const { segment } = useSegment();

  const { year, previousYear, topPorts, shareOfTransits } = statsData || {};

  const renderPercentage = (value) => {
    const num = Number(value);
    if (isNaN(num) || value === '-' || value === null || value === undefined) return '0%';
    return `${num}%`;
  };

  const options = useMemo(() => {
    return {
      selectableRows: 'none',
      fixedHeader: true,
      responsive: 'standard',
      setTableProps: () => {
        return {
          id: 'port-company-transits-table'
        };
      },
      filter: false,
      search: false,
      download: checkUserHasAccess(user, true),
      elevation: 0,
      downloadOptions: {
        filename: fileName.toLowerCase().includes('.csv') ? fileName : `${fileName}.csv`
      },
      print: false,
      viewColumns: false,
      pagination: false,
      toolbar: true,
      tableBodyMaxHeight: '20vh',
      tableBodyHeight: '20vh',
      rowsPerPageOptions: [10, 25, 50, 100]
    };
  }, []);

  const mainHeaders = useMemo(() => {
    return [
      { key: 1, name: ' ', colSpan: 1 },
      { key: 2, name: 'No. of Port Calls', colSpan: 3 },
      { key: 3, name: 'Share of Port Calls', colSpan: 3 }
    ];
  }, []);

  const subHeaders = useMemo(() => {
    return [
      { key: 'portName', name: 'Port Name', direction: '' },
      {
        key: 'totalPreviousYearCount',
        name: `${showYTD ? 'YTD ' : ''}${previousYear}`,
        direction: ''
      },
      { key: 'totalCurrentYearCount', name: `${showYTD ? 'YTD ' : ''}${year}`, direction: '' },
      { key: 'totalIncreasePercent', name: 'Increase %', direction: '' },
      {
        key: 'sharePreviousYear',
        name: `${showYTD ? 'YTD ' : ''}${previousYear}`,
        direction: ''
      },
      { key: 'shareCurrentYear', name: `${showYTD ? 'YTD ' : ''}${year}`, direction: '' },
      { key: 'shareIncreasePercent', name: 'Increase %', direction: '' }
    ];
  }, [year, previousYear]);

  const mergedTableData = useMemo(() => {
    if (!topPorts || !shareOfTransits) return [];

    return topPorts.map((topPort) => {
      const sharePort = shareOfTransits.find((entry) => entry.portName === topPort.portName) || {};

      return {
        portName: topPort.portName,
        portId: topPort.portId,
        totalCurrentYearCount: topPort.currentYearCount,
        totalPreviousYearCount: topPort.previousYearCount,
        totalIncreasePercent: topPort.increasePercent,

        shareCurrentYear: sharePort.shareCurrentYear,
        sharePreviousYear: sharePort.sharePreviousYear,
        shareIncreasePercent: sharePort.increasePercent
      };
    });
  }, [topPorts, shareOfTransits]);

  const sortedTableData = useMemo(() => {
    return [...mergedTableData].sort((firstOp, secondOp) => {
      const col = sortOrder.column;
      if (col !== 'portName') {
        const firstOpVal = Number(firstOp[col]);
        const secondOpVal = Number(secondOp[col]);

        if (sortOrder.direction === 'asc') {
          return firstOpVal - secondOpVal;
        } else {
          return secondOpVal - firstOpVal;
        }
      } else {
        const firstOpVal = firstOp[col] || '';
        const secondOpVal = secondOp[col] || '';
        if (sortOrder.direction === 'asc') return firstOpVal.localeCompare(secondOpVal);
        else return secondOpVal.localeCompare(firstOpVal);
      }
    });
  }, [JSON.stringify(sortOrder), mergedTableData]);

  const handleSortClick = (column) => {
    let direction = 'asc';
    let tempColumn = column;
    if (column === sortOrder.column) {
      if (sortOrder.direction === 'asc') {
        direction = 'desc';
      } else {
        direction = 'asc';
      }
    }
    setSortOrder({ column: tempColumn, direction: direction });
  };

  const handleCellClick = async (yearToQuery, portId) => {
    try {
      const startDate = moment(`${yearToQuery}-01-01`);
      const endDate = moment().year(yearToQuery).endOf('day');

      const queryParams = {
        SegmentTypeId: segment.id,
        StartDate: startDate.format('YYYY-MM-DD'),
        EndDate: endDate.format('YYYY-MM-DD'),
        LastPortCallType: 0,
        ...(companyType === 'operator' && {
          OperatorTypeId: operatorType,
          Operators: [companyId]
        }),
        ...(companyType === 'owner' && {
          Owners: [companyId]
        }),
        CommercialCategoryId: commercialCategoryType,
        GeoIds: [portId],
        geographicalType: GEOGRAPHICAL_TYPES.PORT
      };

      const result = await getLatestPortCalls(queryParams);
      setModalData(result || {});
      setModalOpen(true);
      setModalTitle(`${showYTD ? 'YTD ' : ''}${yearToQuery}`);
    } catch (err) {
      console.error('Failed fetching port calls:', err);
    }
  };

  return (
    <>
      <Paper sx={{ p: 2, ...customScrollbar }} mode={theme.mode}>
        {mergedTableData && (
          <Box
            id="ports-transits-table"
            sx={{
              position: 'relative',
              maxHeight: '75vh',
              overflowY: 'auto',
              ...customScrollbar
            }}>
            <BasicTable stickyHeader aria-label="sticky table" options={options}>
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 2,
                  background: (theme) => theme.palette.background.paper
                }}>
                <TableRow>
                  {[
                    ...mainHeaders.map((val) => (
                      <TableCell key={val.key} align="center" colSpan={val.colSpan}>
                        {val.name}
                      </TableCell>
                    ))
                  ]}
                </TableRow>
              </TableHead>
              <TableHead
                sx={{
                  position: 'sticky',
                  top: 45,
                  zIndex: 1,
                  background: (theme) => theme.palette.background.paper
                }}>
                <TableRow>
                  {[
                    ...subHeaders.map((val) => {
                      let isCurrent = sortOrder.column === val.key;
                      let icon = undefined;
                      if (isCurrent) {
                        if (sortOrder.direction === 'asc') {
                          icon = <ArrowDropUpIcon />;
                        } else {
                          icon = <ArrowDropDownIcon />;
                        }
                      }

                      return (
                        <TableCell
                          key={val.key}
                          align="center"
                          colSpan={1}
                          sx={{
                            height: '100%',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: 'rgba(238,238,238,0.3)'
                            },
                            padding: '0px 16px 0px 16px'
                          }}
                          onClick={() => {
                            handleSortClick(val.key);
                          }}>
                          <Box>
                            <Stack
                              justifyContent={val.key === 'portName' ? 'left' : 'center'}
                              alignItems={val.key === 'portName' ? 'left' : 'center'}
                              direction={'row'}
                              spacing={2}>
                              {val.name}
                              {icon}
                            </Stack>
                          </Box>
                        </TableCell>
                      );
                    })
                  ]}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTableData.map(
                  (
                    {
                      portName,
                      portId,
                      totalPreviousYearCount,
                      totalCurrentYearCount,
                      totalIncreasePercent,
                      sharePreviousYear,
                      shareCurrentYear,
                      shareIncreasePercent
                    },
                    i
                  ) => {
                    return (
                      <TableRow key={`${i}-${portId}`}>
                        <TableCell
                          colSpan={1}
                          align="left"
                          sx={{ minWidth: '10vh', maxWidth: '12vh' }}>
                          <ProfileLink profile={'port'} id={portId} name={portName} />
                        </TableCell>
                        <TableCell
                          colSpan={1}
                          align="center"
                          sx={{
                            minWidth: '10vh',
                            maxWidth: '12vh',
                            cursor: totalPreviousYearCount !== 0 ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (totalPreviousYearCount !== 0) {
                              handleCellClick(previousYear, portId);
                            }
                          }}>
                          {totalPreviousYearCount}
                        </TableCell>
                        <TableCell
                          colSpan={1}
                          align="center"
                          sx={{
                            minWidth: '10vh',
                            maxWidth: '12vh',
                            cursor: totalCurrentYearCount !== 0 ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (totalCurrentYearCount !== 0) {
                              handleCellClick(year, portId);
                            }
                          }}>
                          {totalCurrentYearCount}
                        </TableCell>
                        <TableCell
                          colSpan={1}
                          align="center"
                          sx={{
                            minWidth: '10vh',
                            maxWidth: '12vh',
                            justifyItems: 'center',
                            alignItems: 'center'
                          }}>
                          <Stack
                            direction="row"
                            sx={{
                              minWidth: '10vh',
                              maxWidth: '12vh',
                              justifyItems: 'center',
                              alignItems: 'center'
                            }}>
                            {totalIncreasePercent > 0 && <ArrowDropUpIcon color={'success'} />}
                            {totalIncreasePercent < 0 && <ArrowDropDownIcon color={'error'} />}
                            {renderPercentage(totalIncreasePercent)}
                          </Stack>
                        </TableCell>
                        <TableCell
                          colSpan={1}
                          align="center"
                          sx={{
                            minWidth: '10vh',
                            maxWidth: '12vh',
                            cursor: sharePreviousYear !== 0 ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (sharePreviousYear !== 0) {
                              handleCellClick(previousYear, portId);
                            }
                          }}>
                          {sharePreviousYear}
                        </TableCell>
                        <TableCell
                          colSpan={1}
                          align="center"
                          sx={{
                            minWidth: '10vh',
                            maxWidth: '12vh',
                            cursor: shareCurrentYear !== 0 ? 'pointer' : 'default'
                          }}
                          onClick={() => {
                            if (shareCurrentYear !== 0) {
                              handleCellClick(year, portId);
                            }
                          }}>
                          {shareCurrentYear}
                        </TableCell>
                        <TableCell
                          colSpan={1}
                          align="center"
                          sx={{
                            minWidth: '10vh',
                            maxWidth: '12vh',
                            justifyItems: 'center',
                            alignItems: 'center'
                          }}>
                          <Stack
                            direction={'row'}
                            sx={{
                              minWidth: '10vh',
                              maxWidth: '12vh',
                              justifyItems: 'center',
                              alignItems: 'center'
                            }}>
                            {shareIncreasePercent > 0 && <ArrowDropUpIcon color={'success'} />}
                            {shareIncreasePercent < 0 && <ArrowDropDownIcon color={'error'} />}
                            {renderPercentage(shareIncreasePercent)}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </BasicTable>
          </Box>
        )}
      </Paper>
      {modalOpen && Object.keys(modalData).length > 0 && (
        <UnderlyingCompanyPortModal
          modalOpen={modalOpen}
          modalData={modalData}
          setModalOpen={setModalOpen}
          setModalData={setModalData}
          modalTitle={modalTitle}
        />
      )}
    </>
  );
}

CompanyPortTable.propTypes = {
  loading: PropTypes.bool,
  fileName: PropTypes.string,
  statsData: PropTypes.object,
  companyType: PropTypes.string,
  companyId: PropTypes.number,
  showYTD: PropTypes.bool
};

export default CompanyPortTable;
